import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { generationApi, ChatMessage } from '../services/api';

interface ChatPanelProps {
  provider: 'openai' | 'claude' | 'perplexity';
  initialMessages: ChatMessage[];
  workflowId?: string;
  onConfirm?: (finalText: string) => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ provider, initialMessages, workflowId, onConfirm }) => {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [input, setInput] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [lastModelInfo, setLastModelInfo] = useState<{ provider: string; model: string; tokensUsed?: number } | null>(null);

  const sendMessage = async () => {
    if (!input.trim()) return;
    setSending(true);
    setError('');
    const nextMessages: ChatMessage[] = [...messages, { role: 'user', content: input.trim() }];
    setMessages(nextMessages);
    setInput('');
    try {
      const resp = await generationApi.chat({
        provider,
        messages: nextMessages,
        workflowId,
      });
      const assistantContent: string = resp.content || '';
      setMessages([...nextMessages, { role: 'assistant', content: assistantContent }]);

      // Speichere Model-Informationen für die Anzeige
      if (resp.provider && resp.model) {
        setLastModelInfo({
          provider: resp.provider,
          model: resp.model,
          tokensUsed: resp.tokensUsed,
        });
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Chat fehlgeschlagen');
    } finally {
      setSending(false);
    }
  };

  const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="flex justify-between items-center" style={{ padding: 12, borderBottom: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-bg-secondary)' }}>
        <strong>KI-Chat zur Textverbesserung</strong>
        {lastModelInfo && (
          <div className="text-xs text-muted" style={{ textAlign: 'right' }}>
            <div>
              <strong>Provider:</strong> {lastModelInfo.provider}
            </div>
            <div>
              <strong>Modell:</strong> {lastModelInfo.model}
            </div>
            {lastModelInfo.tokensUsed && (
              <div>
                <strong>Tokens:</strong> {lastModelInfo.tokensUsed}
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{ padding: 12, maxHeight: 300, overflowY: 'auto' }}>
        {messages.map((m, idx) => (
          <div key={idx} className="mb-2">
            <span className="text-xs text-muted">{m.role === 'assistant' ? 'KI' : m.role === 'system' ? 'System' : 'Sie'}</span>
            <div style={{ background: m.role === 'assistant' ? 'var(--sec-surface-alt)' : 'color-mix(in srgb, var(--sec-info) 10%, var(--sec-surface))', padding: 8, borderRadius: 'var(--radius-sm)' }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
            </div>
          </div>
        ))}
        {messages.length === 0 && (
          <div className="text-secondary">Noch keine Nachrichten. Beginnen Sie mit einer Korrekturanfrage.</div>
        )}
      </div>
      {error && (
        <div className="alert alert-error" style={{ margin: 12, marginBottom: 0 }}>{error}</div>
      )}
      <div className="flex gap-2" style={{ padding: 12, borderTop: '1px solid var(--color-border-light)' }}>
        <textarea
          className="form-textarea flex-1"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Beschreiben Sie die gewünschte Verbesserung (Ton, Kürzen, Klarheit...)"
          rows={3}
        />
        <button
          className="btn btn-primary"
          type="button"
          onClick={sendMessage}
          disabled={sending}
        >
          {sending ? 'Senden...' : 'Senden'}
        </button>
      </div>
      {onConfirm && (
        <div className="flex" style={{ padding: 12, borderTop: '1px solid var(--color-border-light)', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-success"
            type="button"
            onClick={() => onConfirm(lastAssistant?.content || '')}
            disabled={!lastAssistant}
          >
            Übernehmen & Weiter
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatPanel;
